import { AsYouType, CountryCode } from 'libphonenumber-js/max';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { PhoneInputCountry } from '../../components/phone-input';
import { COUNTRIES } from '../../components/phone-input/constants';
import { PHONE_MIN_LENGTH } from '../../constants';
import { Nullable, Undefinable } from '../../types';
import { matchIsArray } from '../../util/array';
import { getCallingCodeOfCountry } from '../../util/phone-input';
import { isValidAustralianMobileNumber, isValidPhoneValue } from '../../util/validation';

interface UsePhoneDigitsParams {
  defaultCountry?: PhoneInputCountry;
  disableFormatting?: boolean;
  excludedCountries?: PhoneInputCountry[];
  forceCallingCode?: boolean;
  onChange?: (value: string) => void;
  onlyCountries?: PhoneInputCountry[];
  value: string;
}

interface UsePhoneDigitsReturn {
  callingCode: string;
  inputValue: string;
  isoCode: Nullable<PhoneInputCountry>;
  isValid: boolean;
  onCountryChange: (newCountry: PhoneInputCountry) => void;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

interface State {
  inputValue: string;
  isoCode: Nullable<PhoneInputCountry>;
}

interface GetInitialStateParams {
  defaultCountry?: PhoneInputCountry;
  disableFormatting?: boolean;
  initialValue: string;
}

interface Filters {
  excludedCountries?: PhoneInputCountry[];
  onlyCountries?: PhoneInputCountry[];
}

export const getCountry = (asYouType: AsYouType): Undefinable<CountryCode> => {
  const countryCodes = Object.keys(COUNTRIES) as CountryCode[];
  const value = asYouType.getNumberValue() ?? '';
  const foundCountry: Undefinable<CountryCode> = countryCodes.find((countryCode: CountryCode) => COUNTRIES[countryCode]?.[0] && value.startsWith(`+${COUNTRIES[countryCode]?.[0] as string}`));
  return asYouType.getCountry() ?? foundCountry;
};

export const getInitialState = (params: GetInitialStateParams): State => {
  const { defaultCountry, initialValue, disableFormatting } = params;

  const fallbackValue = defaultCountry ? `+${COUNTRIES[defaultCountry]?.[0] as string}` : '';

  const asYouType = new AsYouType(defaultCountry);
  let inputValue = asYouType.input(initialValue);

  const phoneNumberValue = asYouType.getNumberValue();

  if (defaultCountry && getCountry(asYouType) === undefined) {
    inputValue = fallbackValue;
  } else if (disableFormatting && phoneNumberValue) {
    inputValue = phoneNumberValue;
  }

  return {
    inputValue: inputValue || fallbackValue,
    isoCode: getCountry(asYouType) || defaultCountry || null,
  };
};

const matchIsIsoCodeAccepted = (isoCode: PhoneInputCountry, filters: Filters): boolean => {
  const { excludedCountries, onlyCountries } = filters;
  if (matchIsArray(excludedCountries, true) && excludedCountries.includes(isoCode)) {
    return false;
  }
  if (matchIsArray(onlyCountries) && !onlyCountries.includes(isoCode)) {
    return false;
  }
  return true;
};

export const usePhoneDigits = ({
  value,
  onChange,
  defaultCountry,
  forceCallingCode,
  onlyCountries,
  excludedCountries,
  disableFormatting,
}: UsePhoneDigitsParams): UsePhoneDigitsReturn => {
  const asYouTypeRef = useRef<AsYouType>(new AsYouType(defaultCountry));
  const [previousDefaultCountry, setPreviousDefaultCountry] = useState(defaultCountry);
  const [state, setState] = useState<State>(() => {
    return getInitialState({
      initialValue: value,
      defaultCountry,
      disableFormatting,
    });
  });
  const [previousValue, setPreviousValue] = useState(value);

  const matchIsIsoCodeValid = (isoCode: Nullable<PhoneInputCountry>): Nullable<boolean> => {
    return (
      isoCode &&
      matchIsIsoCodeAccepted(isoCode, {
        onlyCountries,
        excludedCountries,
      })
    );
  };

  const typeNewValue = (inputValue: string): string => {
    asYouTypeRef.current.reset();
    return asYouTypeRef.current.input(inputValue);
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    let inputVal = event.target.value;
    inputVal = inputVal.startsWith('+') || inputVal === '' ? inputVal : `+${inputVal}`;
    const formattedValue = typeNewValue(inputVal);
    const country = getCountry(asYouTypeRef.current) ?? null;
    const phoneNumber = asYouTypeRef.current.getNumber() ?? null;
    const numberValue = asYouTypeRef.current.getNumberValue() ?? '';
    const isFallbackAustralianMobile = isValidAustralianMobileNumber(numberValue);
    const effectiveCountry: Nullable<PhoneInputCountry> = country ?? (isFallbackAustralianMobile ? 'AU' : null);
    const emitNumberValue = phoneNumber ? numberValue : (isFallbackAustralianMobile ? numberValue : '');
    const emitFormattedValue = phoneNumber ? formattedValue : (isFallbackAustralianMobile ? (formattedValue || numberValue) : '');

    if (forceCallingCode && !phoneNumber && (state.isoCode || defaultCountry)) {
      const inputValueIsoCode = `+${getCallingCodeOfCountry(
        state.isoCode || (defaultCountry as PhoneInputCountry),
      )}`;
      onChange?.(inputValueIsoCode);
      setPreviousValue(inputValueIsoCode);
      setState({
        isoCode: state.isoCode || defaultCountry || null,
        inputValue: inputValueIsoCode,
      });
    } else if (numberValue && (!effectiveCountry || !matchIsIsoCodeValid(effectiveCountry))) {
      onChange?.(emitNumberValue);
      setPreviousValue(emitNumberValue);
      setState({
        isoCode: null,
        inputValue: numberValue,
      });
    } else if (disableFormatting) {
      onChange?.(emitNumberValue);
      setPreviousValue(emitNumberValue);
      setState({
        isoCode: effectiveCountry,
        inputValue: numberValue,
      });
    } else {
      onChange?.(emitFormattedValue);
      setPreviousValue(emitFormattedValue);
      setState({
        isoCode: effectiveCountry,
        inputValue: formattedValue,
      });
    }
  };

  useEffect(() => {
    if (value !== previousValue) {
      setPreviousValue(value);
      setState(
        getInitialState({
          initialValue: value,
          defaultCountry,
        }),
      );
    }
  }, [value, previousValue, defaultCountry]);

  useEffect(() => {
    if (defaultCountry === previousDefaultCountry) return;
    // @ts-expect-error `country` is not a documented property but it exists runtime.
    // Probably could be changed to `getCountry()` but it behaves in a slightly different way.
    if (asYouTypeRef.current.country === previousDefaultCountry) return;
    setPreviousDefaultCountry(defaultCountry);
    asYouTypeRef.current = new AsYouType(defaultCountry);
    const { inputValue, isoCode } = getInitialState({
      initialValue: '',
      defaultCountry,
    });
    asYouTypeRef.current.input(inputValue);
    if (value) {
      setPreviousValue(inputValue);
      onChange?.(inputValue);
    } else {
      setPreviousValue(value);
    }
    setState({
      inputValue,
      isoCode,
    });
  }, [defaultCountry, previousDefaultCountry, onChange, value]);

  const onCountryChange = (newCountry: PhoneInputCountry): void => {
    if (newCountry === state.isoCode) {
      return;
    }
    const callingCode = COUNTRIES[newCountry]?.[0] as string;
    const formattedValue = typeNewValue(`+${callingCode}`);
    onChange?.('');
    setPreviousValue('');
    setState({
      isoCode: newCountry,
      inputValue: formattedValue,
    });
  };

  const isValid: boolean = state.inputValue && state.inputValue.length > PHONE_MIN_LENGTH ? isValidPhoneValue(state.inputValue) : true;

  return {
    callingCode: state.isoCode && COUNTRIES[state.isoCode]?.[0] ? `+${COUNTRIES[state.isoCode]?.[0] as string}` : '',
    inputValue: state.inputValue,
    isoCode: state.isoCode,
    onInputChange,
    onCountryChange,
    isValid,
  };
};
